import { TooltipContent } from '@radix-ui/react-tooltip';
import { TooltipTrigger } from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';
import { AppNav } from './AppNav';
import { Link } from 'react-router-dom';
import { Tooltip } from '@radix-ui/react-tooltip';
import { IconComponent } from '@/types/common';

export interface SectionDefinition {
  id: string;
  title: string;
  href: string;
  Icon: IconComponent;
}

export function SectionalContainer({
  navStartSlot,
  navEndSlot,
  currentSection,
  children,
  sections,
  name,
}: {
  children: React.ReactNode;
  currentSection: string;
  sections: SectionDefinition[];
  name?: string;
  navStartSlot?: React.ReactNode;
  navEndSlot?: React.ReactNode;
}) {
  return (
    <div
      data-name={name || 'SECTIONAL_CONTAINER'}
      className="flex min-h-screen flex-col pb-32 md:pb-0"
    >
      <AppNav />
      <div
        className={`min-w-screen relative z-[2] mt-0 flex flex-1 flex-col justify-center`}
      >
        <SectionalNav
          name={name}
          currentSection={currentSection}
          sections={sections}
          startSlot={navStartSlot}
          endSlot={navEndSlot}
        />

        <main data-name={`${name}_CONTENT_CONTAINER`} className={`flex flex-1`}>
          <div
            data-name={`${name}_CONTENT`}
            className={`flex-1 rounded-lg md:p-4`}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

function SectionalNav({
  currentSection,
  sections,
  name,
  startSlot,
  endSlot,
}: {
  currentSection: string;
  sections: SectionDefinition[];
  name?: string;
  startSlot?: React.ReactNode;
  endSlot?: React.ReactNode;
}) {
  return (
    <div
      data-name={`${name || 'SECTIONAL'}_NAV_CONTAINER`}
      className={`flex flex-row items-end justify-between border-b md:px-6 md:pt-1`}
    >
      <div className="flex flex-row items-center justify-between gap-2 md:gap-8">
        {startSlot}
        <nav
          data-name={`${name || 'SECTIONAL'}_NAV`}
          className={cn(
            `flex space-x-2`,
            'items-center justify-center',
            'fixed bottom-0 left-0 right-0',
            'bg-background',
            'md:bg-transparent',
            'border-t md:border-t-0',
            'md:relative md:bottom-auto md:left-auto md:right-auto',
            'z-10 md:z-auto',
            'w-full md:w-auto',
            'h-[7vh] md:h-auto',
          )}
        >
          {sections.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                currentSection === item.id
                  ? `bg-epic text-epic-foreground md:border-border md:bg-background md:text-foreground`
                  : `border-transparent text-muted-foreground hover:bg-transparent`,
                'relative top-[1px] justify-start hover:text-foreground',
                'px-4 md:py-2',
                'h-full',
                'flex items-center',
                'md:rounded-tl-lg md:rounded-tr-lg',
                'border-l border-r border-t',
              )}
            >
              <div className="flex flex-row items-center space-x-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <item.Icon className={cn('size-6 md:size-4')} />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{item.title}</p>
                  </TooltipContent>
                </Tooltip>
                {/* only show title on desktop */}
                <div className={`hidden lg:block`}>{item.title}</div>
              </div>
            </Link>
          ))}
        </nav>
      </div>
      {endSlot}
    </div>
  );
}
